import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useScanInput } from '../hooks/useScanInput';
import { useAudioFeedback } from '../hooks/useAudioFeedback';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { inputClass } from '../components/ui/Input';

interface ZoneRow {
  id: string;
  sku: string;
  name: string;
  ean: string | null;
  /** zone-aisle-shelf: one row per LOCATION, so a part on two shelves is counted at each. */
  locationId: string;
  /** Physical on-hand of the product (available + held), what the count is compared with. */
  expectedQty: number;
  /** Other zones the part is also stored in — its count must cover them too. */
  otherZones: string[];
}

const keyOf = (r: ZoneRow) => `${r.id}|${r.locationId}`;

/**
 * Screen 29 — Zone Inventory (tablet). Pick zone → count (scan, +/−, type, or "absent") →
 * validate.
 *
 * 2026-10-23 fixes: the expected figure was read from a field the API never sent (shown as NaN),
 * and validating sent 0 for every row nobody had counted — a partial count of a zone set the rest
 * of it to zero. Now a row is only sent once it has been counted; "Absent" is the explicit zero.
 */
export default function ZoneInventory() {
  const { t } = useTranslation();
  const beep = useAudioFeedback();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [zone, setZone] = useState<'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'P' | 'Q' | 'R'>('A');
  const [rows, setRows] = useState<ZoneRow[]>([]);
  /** Row key → counted quantity. A row absent from the map has not been counted. */
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  const load = (z: string) =>
    apiClient
      .get<ZoneRow[]>(`/warehouse/inventory/zone/${z}`)
      .then((r) => setRows(r.data ?? []))
      .catch(showApiError);

  useEffect(() => {
    setCounts({});
    void load(zone);
  }, [zone]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, n: number | null) =>
    setCounts((c) => {
      const next = { ...c };
      if (n === null) delete next[k];
      else next[k] = Math.max(0, n);
      return next;
    });

  /* Matched against the zone's own rows (EAN, then SKU): the zone listing carries both. It used
     to ask the sales catalogue search, which a warehouse keeper may not use (SALES_SEARCH) — so
     on the tablet every scan was refused as an unknown part. */
  useScanInput({
    onScan: (raw) => {
      const code = raw.trim();
      const norm = (v: string | null) => (v ?? '').replace(/^0+/, '');
      const row =
        rows.find((p) => p.ean && norm(p.ean) === norm(code)) ??
        rows.find((p) => p.sku.toUpperCase() === code.toUpperCase());
      if (!row) {
        beep.wrongProduct();
        push({ tone: 'warning', message: t('warehouse.zoneInventory.notInZone') });
        return;
      }
      beep.success();
      setCounts((c) => ({ ...c, [keyOf(row)]: (c[keyOf(row)] ?? 0) + 1 }));
    },
  });

  /** Per product: counted so far (over its rows in this zone) and how many rows are counted. */
  const byProduct = useMemo(() => {
    const m = new Map<string, { counted: number; rowsCounted: number; rows: number }>();
    for (const r of rows) {
      const cur = m.get(r.id) ?? { counted: 0, rowsCounted: 0, rows: 0 };
      cur.rows += 1;
      const n = counts[keyOf(r)];
      if (n !== undefined) {
        cur.counted += n;
        cur.rowsCounted += 1;
      }
      m.set(r.id, cur);
    }
    return m;
  }, [rows, counts]);

  const countedRows = rows.filter((r) => counts[keyOf(r)] !== undefined);
  // A product is sent only once every one of its locations here is counted: stock is kept per
  // product, so half of its shelves would adjust the whole of it to that half.
  const sendable = countedRows.filter((r) => {
    const p = byProduct.get(r.id)!;
    return p.rowsCounted === p.rows;
  });
  const partial = countedRows.length - sendable.length;

  const submit = async () => {
    const ok = await confirm({
      title: t('warehouse.zoneInventory.confirmTitle', { zone }),
      description:
        t('warehouse.zoneInventory.confirmBody', {
          counted: sendable.length,
          left: rows.length - sendable.length,
        }) +
        (partial > 0 ? ` ${t('warehouse.zoneInventory.partialNote', { count: partial })}` : ''),
      confirmLabel: t('warehouse.zoneInventory.submit'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      const body = {
        zone,
        counts: sendable.map((r) => ({
          productId: r.id,
          locationId: r.locationId,
          actualQty: counts[keyOf(r)],
        })),
      };
      const { data } = await apiClient.post<{
        adjustments: { productId: string; delta: number }[];
      }>('/warehouse/inventory/validate', body, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({
        tone: 'success',
        message: t('warehouse.zoneInventory.adjustments', { count: data.adjustments.length }),
      });
      setCounts({});
      await load(zone);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">
          {t('warehouse.zoneInventory.title')}
        </h1>
        <select
          value={zone}
          onChange={(e) => setZone(e.target.value as typeof zone)}
          className={inputClass}
          style={{ width: 'auto', minWidth: 140 }}
          aria-label={t('warehouse.zoneInventory.zone')}
        >
          {['A', 'B', 'C', 'D', 'E', 'F', 'P', 'Q', 'R'].map((z) => (
            <option key={z} value={z}>
              {t('warehouse.zoneInventory.zoneOption', { zone: z })}
            </option>
          ))}
        </select>
        <span className="ml-auto text-sm text-muted-foreground">
          {t('warehouse.zoneInventory.progress', {
            counted: countedRows.length,
            total: rows.length,
          })}
        </span>
      </header>
      <p className="text-sm text-muted-foreground">{t('warehouse.zoneInventory.hint')}</p>

      <ul className="border rounded divide-y bg-card">
        {rows.map((r) => {
          const k = keyOf(r);
          const n = counts[k];
          const prod = byProduct.get(r.id)!;
          // The difference is a PRODUCT fact: shown once every location of it here is counted.
          const complete = prod.rowsCounted === prod.rows;
          const diff = complete ? prod.counted - r.expectedQty : null;
          return (
            <li key={k} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {r.sku}
                  {r.ean ? ` · ${r.ean}` : ''} · {r.locationId}
                </div>
                {r.otherZones.length > 0 && (
                  <div className="text-xs text-warning">
                    {t('warehouse.zoneInventory.alsoIn', { zones: r.otherZones.join(', ') })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-lg">
                <div className="text-muted-foreground text-sm">
                  {t('warehouse.zoneInventory.expected', { qty: r.expectedQty })}
                </div>
                <button
                  type="button"
                  onClick={() => set(k, (n ?? 0) - 1)}
                  className="border rounded w-10 h-10 text-lg"
                  aria-label="−"
                >
                  −
                </button>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  aria-label={t('warehouse.zoneInventory.counted')}
                  placeholder="—"
                  value={n ?? ''}
                  onChange={(e) =>
                    set(k, e.target.value === '' ? null : Math.floor(Number(e.target.value)))
                  }
                  className={inputClass + ' text-center font-mono'}
                  style={{ width: 80 }}
                />
                <button
                  type="button"
                  onClick={() => set(k, (n ?? 0) + 1)}
                  className="border rounded w-10 h-10 text-lg"
                  aria-label="+"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => set(k, 0)}
                  className="border rounded h-10 px-3 text-sm"
                >
                  {t('warehouse.zoneInventory.absent')}
                </button>
                <span
                  className={`w-14 text-right text-sm ${diff === null ? 'text-muted-foreground/60' : diff > 0 ? 'text-success' : diff < 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {diff === null ? '' : diff > 0 ? `+${diff}` : diff === 0 ? '=' : diff}
                </span>
              </div>
            </li>
          );
        })}
        {rows.length === 0 && (
          <li className="px-4 py-3 text-sm text-muted-foreground">
            {t('warehouse.zoneInventory.empty', { zone })}
          </li>
        )}
      </ul>

      <button
        onClick={() => void submit()}
        disabled={sendable.length === 0 || busy}
        className="bg-success text-primary-foreground rounded px-4 py-2 disabled:opacity-40"
      >
        {t('warehouse.zoneInventory.submit')}
      </button>
      {confirmDialog}
    </div>
  );
}
